import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Stack,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  DndContext,
  useSensors,
  useSensor,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';
import CreateButton from '../../../components/button/CreateButton';
import BasicButton from '../../../components/button/BasicButton';
import EditButton from '../../../components/button/EditButton';
import { adminApi } from '../../../api/admin';
import ProgressBar from '../../../components/common/ProgressBar';
import NoData from '../../../components/common/NoData';
import SortableBannerRow from './SortableBannerRow';
import { useUserStore } from '../../../stores/userStore';
import useAdminDraftGuard from '../useAdminDraftGuard';

const HEAD_CELLS = [
  { id: 'imageUrl', label: '대표 이미지' },
  { id: 'title', label: '제목' },
  { id: 'url', label: 'URL' },
  { id: 'clickCount', label: '클릭수' },
  { id: 'edit', label: '편집' },
];

const BannerSection = ({
  deviceType,
  banners,
  selectedBannerIds,
  onSelectAll,
  onSelect,
  onEdit,
  onCreate,
  onDelete,
  onOrderUpdate,
  busy,
  cellStyle,
  textStyle,
  buttonStyle,
}) => {
  const theme = useTheme();
  const pending = useRef(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [orderedBanners, setOrderedBanners] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const draft = useAdminDraftGuard(
    orderedBanners.map((banner) => banner.id),
    { enabled: isEditingOrder, busy: savingOrder },
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleStartEditOrder = () => {
    setOrderedBanners([...banners]);
    setIsEditingOrder(true);
  };

  const handleCancelEditOrder = () => {
    if (!draft.confirmDiscard()) return;
    setIsEditingOrder(false);
    setOrderedBanners([]);
  };

  const handleSaveOrder = async () => {
    if (pending.current || busy) return;
    pending.current = true;
    setSavingOrder(true);
    const bannerIds = orderedBanners.map((b) => b.id);
    try {
      if ((await onOrderUpdate(bannerIds)) && live.current) {
        draft.markClean();
        setIsEditingOrder(false);
      }
    } finally {
      pending.current = false;
      if (live.current) setSavingOrder(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!isEditingOrder || pending.current || busy || !over) return;
    if (active.id !== over?.id) {
      setOrderedBanners((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const displayBanners = isEditingOrder ? orderedBanners : banners;

  const numSelectedOnPage = useMemo(
    () => banners.filter((banner) => selectedBannerIds.includes(banner.id)).length,
    [banners, selectedBannerIds],
  );

  const isAllSelected = banners.length > 0 && numSelectedOnPage === banners.length;
  const isIndeterminate = numSelectedOnPage > 0 && !isAllSelected;
  const hasSelection = selectedBannerIds.some((id) => banners.some((b) => b.id === id));

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* 액션 버튼 */}
      <Stack
        component="fieldset"
        disabled={savingOrder || busy}
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          m: 0,
          pb: 2,
          border: 0,
          p: 0,
          minWidth: 0,
          flexWrap: 'wrap',
          gap: 1,
          '& > *': { marginLeft: '0 !important' },
        }}
      >
        {isEditingOrder ? (
          <>
            <BasicButton variant="outlined" onClick={handleSaveOrder} sx={buttonStyle}>
              저장
            </BasicButton>
            <BasicButton variant="outlined" onClick={handleCancelEditOrder} sx={buttonStyle}>
              취소
            </BasicButton>
          </>
        ) : (
          <>
            <CreateButton text="배너" onClick={() => onCreate(deviceType)} sx={buttonStyle} />
            <BasicButton
              variant="outlined"
              onClick={handleStartEditOrder}
              disabled={banners.length < 2}
              sx={buttonStyle}
            >
              순서 변경
            </BasicButton>
            <BasicButton
              variant="outlined"
              onClick={onDelete}
              disabled={!hasSelection}
              sx={buttonStyle}
            >
              삭제
            </BasicButton>
          </>
        )}
      </Stack>

      {/* 테이블 */}
      <TableContainer
        component={Paper}
        sx={{ maxWidth: '100%', width: '100%', minWidth: 0, overflowX: 'auto', boxShadow: 'none' }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                <TableCell padding="checkbox" sx={cellStyle}>
                  {isEditingOrder ? (
                    <Box sx={{ width: 42, height: 42 }} />
                  ) : (
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={(e) => onSelectAll(e, deviceType)}
                      size="small"
                      disabled={busy || banners.length === 0}
                      inputProps={{ 'aria-label': '모든 배너 선택' }}
                      sx={{
                        color: theme.palette.grey.line,
                        '&.Mui-checked': { color: '#f16438' },
                        '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                      }}
                    />
                  )}
                </TableCell>
                {HEAD_CELLS.filter((cell) => !isEditingOrder || cell.id !== 'edit').map(
                  (headCell, index, arr) => (
                    <TableCell
                      key={headCell.id}
                      align="center"
                      sx={{
                        whiteSpace: 'nowrap',
                        borderRight:
                          index === arr.length - 1
                            ? 'none'
                            : `1px solid ${theme.palette.grey.line}`,
                      }}
                    >
                      <Typography fontSize={14} color={theme.palette.black[4]}>
                        {headCell.label}
                      </Typography>
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isEditingOrder ? (
                <SortableContext
                  items={orderedBanners.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedBanners.map((banner) => (
                    <SortableBannerRow
                      key={banner.id}
                      banner={banner}
                      disabled={savingOrder || busy}
                      cellStyle={cellStyle}
                      textStyle={textStyle}
                    />
                  ))}
                </SortableContext>
              ) : (
                displayBanners.map((banner) => {
                  const isItemSelected = selectedBannerIds.includes(banner.id);
                  return (
                    <TableRow
                      key={banner.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox" sx={cellStyle}>
                        <Checkbox
                          disabled={busy}
                          inputProps={{
                            'aria-label': `${banner.appConfig?.headline || banner.title} 선택`,
                          }}
                          checked={isItemSelected}
                          onClick={(event) => onSelect(event, banner.id)}
                          size="small"
                          sx={{
                            color: theme.palette.grey.line,
                            '&.Mui-checked': { color: '#f16438' },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          ...cellStyle,
                          minWidth: 224,
                          width: 224,
                          height: 144,
                          p: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          component="img"
                          src={banner.appConfig?.imageUrl || banner.imageUrl}
                          alt={banner.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, minWidth: 300, width: 300 }}>
                        <Typography {...textStyle}>
                          {banner.appConfig?.headline || banner.title}
                        </Typography>
                        {banner.appConfig && (
                          <Typography variant="caption">
                            앱·웹 {banner.appConfig.enabled !== false ? '공개' : '비공개'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Link
                          href={banner.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none' }}
                        >
                          <Typography
                            {...textStyle}
                            sx={{
                              maxWidth: 300,
                              width: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {banner.url}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        <Typography {...textStyle}>{banner.clickCount.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 0 }}>
                        <Box component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0 }}>
                          <EditButton onToggle={() => onEdit(banner)} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
      {banners.length === 0 && <NoData message="등록된 배너가 없습니다" />}
    </Box>
  );
};

const parseBanners = (response) => {
  const items = response?.data?.MOBILE;
  if (
    !Array.isArray(items) ||
    items.some(
      (b) =>
        !b ||
        !Number.isSafeInteger(b.id) ||
        typeof b.title !== 'string' ||
        typeof b.url !== 'string' ||
        !Number.isFinite(b.clickCount),
    ) ||
    new Set(items.map((b) => b.id)).size !== items.length
  )
    throw new Error('배너 목록 응답을 확인하지 못했습니다.');
  return items;
};
const BannerManagement = () => {
  const user = useUserStore((state) => state.user);
  return <BannerSession key={JSON.stringify([user?.id, user?.role])} />;
};
const BannerSession = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobileBanners, setMobileBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedBannerIds, setSelectedBannerIds] = useState([]);
  const [deleteIds, setDeleteIds] = useState(null);
  const pending = useRef(false);
  const live = useRef(true);
  const sequence = useRef(0);
  useAdminDraftGuard(null, { busy });
  const load = useCallback(async (background = false) => {
    const seq = ++sequence.current;
    if (!background) setLoading(true);
    setLoadError('');
    try {
      const items = parseBanners(await adminApi.getBanners());
      if (!live.current || sequence.current !== seq) return;
      setMobileBanners(items);
      setSelectedBannerIds((ids) => ids.filter((id) => items.some((b) => b.id === id)));
    } catch (err) {
      if (live.current && sequence.current === seq)
        setLoadError(
          background
            ? '변경은 저장됐지만 최신 목록을 불러오지 못했습니다. 다시 조회해주세요.'
            : '배너 목록을 불러오지 못했습니다. 다시 시도해주세요.',
        );
    } finally {
      if (live.current && sequence.current === seq) setLoading(false);
    }
  }, []);
  useEffect(() => {
    live.current = true;
    load();
    return () => {
      live.current = false;
    };
  }, [load]);

  const mutate = async (action, apply, message) => {
    if (pending.current) return false;
    pending.current = true;
    sequence.current++;
    setBusy(true);
    setActionError('');
    setNotice('');
    try {
      await action();
      if (!live.current) return false;
      apply();
      setSelectedBannerIds([]);
      setNotice(message);
      // The write already succeeded: refresh failures must never look like failed writes.
      void Promise.resolve(queryClient.invalidateQueries({ queryKey: ['home-banners'] })).catch(
        () => {},
      );
      await load(true);
      return live.current;
    } catch (err) {
      if (live.current)
        setActionError(
          err.response?.data?.msg || err.message || '변경하지 못했습니다. 다시 시도해주세요.',
        );
      return false;
    } finally {
      pending.current = false;
      if (live.current) setBusy(false);
    }
  };
  const handleSelectAllClick = (event) => {
    if (!pending.current)
      setSelectedBannerIds(event.target.checked ? mobileBanners.map((b) => b.id) : []);
  };
  const handleSelectClick = (_event, id) => {
    if (!pending.current)
      setSelectedBannerIds((ids) =>
        ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id],
      );
  };
  const handleDelete = () => {
    if (!deleteIds?.length) return;
    const ids = [...deleteIds];
    return mutate(
      () => adminApi.deleteBanners(ids),
      () => {
        setMobileBanners((items) => items.filter((b) => !ids.includes(b.id)));
        setDeleteIds(null);
      },
      `${ids.length}개의 배너를 삭제했습니다.`,
    );
  };
  const handleOrderUpdate = (ids) =>
    mutate(
      () => adminApi.updateBannerOrder(ids),
      () => {
        setMobileBanners((items) =>
          ids.map((id) => items.find((b) => b.id === id)).filter(Boolean),
        );
      },
      '배너 순서를 저장했습니다.',
    );
  const cellStyle = { borderRight: `1px solid ${theme.palette.grey.line}` };
  const textStyle = { fontSize: 14, color: theme.palette.black[9] };
  const buttonStyle = { typography: 'body3', fontWeight: 'medium', padding: '12px 16px' };
  return (
    <ProgressBar isLoading={loading}>
      <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2, flexWrap: 'wrap', '& .MuiAlert-message': { minWidth: 0 } }}
            action={
              <Button disabled={busy} onClick={() => load(Boolean(notice))}>
                재시도
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        {actionError && !deleteIds && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        {busy && <Typography role="status">변경사항을 처리하는 중…</Typography>}
        {(!loadError || mobileBanners.length > 0 || notice) && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              앱과 웹이 같은 상단 배너와 순서를 사용합니다. 모바일은 1개 중심, 태블릿은 2개, PC는
              3개를 나란히 표시하며 나머지는 넘겨볼 수 있습니다. 공개·이미지·문구·AD 설정은
              공통입니다.
            </Alert>
            <Typography
              fontSize={{ xs: 18, md: 20, lg: 24 }}
              fontWeight="bold"
              color={theme.palette.black[9]}
              sx={{ mb: { xs: '20px', md: '30px' } }}
            >
              홈 상단 배너 · 앱·웹 공통
            </Typography>
            <BannerSection
              deviceType="MOBILE"
              banners={mobileBanners}
              selectedBannerIds={selectedBannerIds}
              onSelectAll={handleSelectAllClick}
              onSelect={handleSelectClick}
              onEdit={(banner) => {
                if (!pending.current)
                  navigate(`/admin/banners/${banner.id}/edit?deviceType=MOBILE`);
              }}
              onCreate={() => {
                if (!pending.current) navigate('/admin/banners/create');
              }}
              onDelete={() => {
                if (!pending.current) {
                  setActionError('');
                  setDeleteIds([...selectedBannerIds]);
                }
              }}
              onOrderUpdate={handleOrderUpdate}
              busy={busy || Boolean(loadError)}
              cellStyle={cellStyle}
              textStyle={textStyle}
              buttonStyle={buttonStyle}
            />
          </>
        )}
        <Dialog
          open={deleteIds !== null}
          onClose={() => {
            if (!pending.current) {
              setDeleteIds(null);
              setActionError('');
            }
          }}
          aria-labelledby="banner-delete-title"
          PaperProps={{
            sx: { m: 2, maxWidth: 'calc(100% - 32px)', minWidth: 0, overflowWrap: 'anywhere' },
          }}
        >
          <DialogTitle id="banner-delete-title" sx={{ px: 1.5 }}>
            배너 삭제
          </DialogTitle>
          <DialogContent sx={{ px: 1.5 }}>
            {actionError && (
              <Alert
                severity="error"
                sx={{
                  p: 1,
                  flexWrap: 'wrap',
                  '& .MuiAlert-message': { minWidth: 0, flex: '1 1 110px' },
                }}
              >
                {actionError}
              </Alert>
            )}
            선택한 {deleteIds?.length || 0}개의 배너를 삭제하시겠습니까?
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              disabled={busy}
              onClick={() => {
                setDeleteIds(null);
                setActionError('');
              }}
            >
              취소
            </Button>
            <Button disabled={busy} onClick={handleDelete}>
              삭제
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProgressBar>
  );
};
export default BannerManagement;
